import { Box, Select, Skeleton, Text, Textarea, TextInput } from "@dehub/theme";
import type { UseFormReturnType } from "@dehub/theme";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import bz from "@/features/business/business.module.css";
import GoogleMapPin from "@/components/google-map";
import type { LatLng } from "@/components/google-map";
import type { IGeneral } from "@/models/General";
import classes from "../branch.module.css";
import { ADDRESS_DESCRIPTION_MAX, ADDRESS_PART_MAX, type BranchAddressValues } from "./branch-form-model";

type Props = Readonly<{
  form: UseFormReturnType<BranchAddressValues>;
  readOnly: boolean;
}>;

type Zip = { code: string; name: string; parent?: string };

const asNumber = (text: string) => (text.trim() === "" || Number.isNaN(Number(text)) ? null : Number(text));

export function BranchAddressStep({ form, readOnly }: Props) {
  const { t } = useTranslation("sector");
  const { t: tCommon } = useTranslation("common");
  const zipCodes = useSelector((state: { general: IGeneral }) => state.general.zipCodes) as Zip[];

  const options = (parent: string) =>
    (zipCodes ?? []).filter((item) => item.parent === parent).map((item) => ({ label: item.name, value: item.code }));

  const lat = asNumber(form.values.locationLat);
  const lng = asNumber(form.values.locationLng);
  const center = lat !== null && lng !== null ? { lat, lng } : null;

  // The map and the two fields are one value: a click, drag or search writes the fields; typing moves the pin.
  const handleMap = (position: LatLng) => {
    form.setFieldValue("locationLat", position.lat.toFixed(6));
    form.setFieldValue("locationLng", position.lng.toFixed(6));
  };

  return (
    <Box className={bz.stack}>
      <Text component="span" className={bz.caps}>
        {tCommon("tab.address")}
      </Text>
      <Box className={classes.addressGrid}>
        <Select
          required
          data-path="province"
          name="province"
          data={options("0")}
          label={tCommon("address.province")}
          placeholder={tCommon("select")}
          readOnly={readOnly}
          searchable
          {...form.getInputProps("province")}
          onChange={(value) => {
            form.setFieldValue("province", value ?? "");
            form.setFieldValue("district", "");
            form.setFieldValue("khoroo", "");
          }}
        />
        <Select
          required
          data-path="district"
          name="district"
          disabled={!form.values.province}
          data={options(form.values.province)}
          label={tCommon("address.district")}
          placeholder={tCommon("select")}
          readOnly={readOnly}
          searchable
          {...form.getInputProps("district")}
          onChange={(value) => {
            form.setFieldValue("district", value ?? "");
            form.setFieldValue("khoroo", "");
          }}
        />
        <Select
          required
          name="khoroo"
          disabled={!form.values.district}
          data={options(form.values.district)}
          label={tCommon("address.khoroo")}
          placeholder={tCommon("select")}
          readOnly={readOnly}
          searchable
          {...form.getInputProps("khoroo")}
        />
        <TextInput required name="khoroolol" maxLength={ADDRESS_PART_MAX} label={tCommon("address.street")} readOnly={readOnly} {...form.getInputProps("khoroolol")} />
        <TextInput required name="khotkhonBair" maxLength={ADDRESS_PART_MAX} label={tCommon("address.building")} readOnly={readOnly} {...form.getInputProps("khotkhonBair")} />
        <TextInput required name="khashaaDavkhar" maxLength={ADDRESS_PART_MAX} label={tCommon("address.fence")} readOnly={readOnly} {...form.getInputProps("khashaaDavkhar")} />
        <TextInput required name="khaalgaDugaar" maxLength={ADDRESS_PART_MAX} label={tCommon("address.door")} readOnly={readOnly} {...form.getInputProps("khaalgaDugaar")} />
        <Box className={classes.addressWide}>
          <Textarea
            required
            name="branchAddress"
            autosize
            minRows={2}
            maxLength={ADDRESS_DESCRIPTION_MAX}
            label={tCommon("address.description")}
            readOnly={readOnly}
            {...form.getInputProps("branchAddress")}
          />
        </Box>
      </Box>

      <Box className={bz.capsRow}>
        <Text component="span" className={bz.caps}>
          {t("form.location")}
        </Text>
        <Text component="span" className={bz.hint}>
          {readOnly ? t("form.mapHintReadOnly") : t("form.mapHint")}
        </Text>
      </Box>
      <Box className={classes.mapBox}>
        <Skeleton className={classes.mapSkeleton} height="100%" radius={0} />
        <Box className={classes.mapLayer}>
          <GoogleMapPin center={center} zoom={16} readOnly={readOnly} onChange={handleMap} />
        </Box>
      </Box>
      <Box className={bz.fieldGrid}>
        <TextInput
          required
          name="locationLat"
          inputMode="decimal"
          autoComplete="off"
          className={classes.mono}
          label={t("form.latitude")}
          readOnly={readOnly}
          {...form.getInputProps("locationLat")}
        />
        <TextInput
          required
          name="locationLng"
          inputMode="decimal"
          autoComplete="off"
          className={classes.mono}
          label={t("form.longitude")}
          readOnly={readOnly}
          {...form.getInputProps("locationLng")}
        />
      </Box>
    </Box>
  );
}
